import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Box,
  Stack,
  useTheme,
  DarkTooltip,
  useMediaQuery,
} from '@ltvco/refresh-lib/theme';
import {
  SerializedPerson,
  serializedTypes,
  commonTypes,
} from '@ltvco/refresh-lib/v1';
import {
  FileCopy,
  FileCopyOutlined,
  ThumbDownOffAlt,
  ThumbUp,
  ThumbUpOffAlt,
  ThumbDown,
} from '@mui/icons-material';
import { useState, useRef, useEffect, useContext, useCallback } from 'react';

import {
  MessageBoxContainer,
  MessageContainerDivider,
  ChatPageContainerStack,
  MessagesContentStack,
  MessageContent,
  RelatedReportsChat,
  ReportColumn,
  TeaserColumn,
  TypingIndicator,
  FiltersColumn,
  ChatFeedbackModal,
  ChatInput,
} from 'components/ChatBV/components';
import { CHATBV_GA4_EVENTS, LOADING_STATUS } from 'components/ChatBV/constants';
import {
  useFeedback,
  useReportState,
  useChatForm,
} from 'components/ChatBV/hooks';
import { LoadingStatus, Message } from 'components/ChatBV/types';
import { copyProcessedText } from 'components/ChatBV/utils';

import { LandingSection } from './LandingSection';

type MessagesContainerProps = {
  messages: Message[];
  loadingStatus: LoadingStatus;
  onReportStateChange?: (isOpen: boolean) => void; // eslint-disable-line no-unused-vars
  shouldCloseReport?: boolean;
  onReportCloseHandled?: () => void;
  sx?: any;
  showLanding?: boolean;
  isLogoAnimating?: boolean;
  logoAnimationPhase?: 'center' | 'moving-up' | 'moving-left' | 'header';
  onSendMessage?: (message: string) => void; // eslint-disable-line no-unused-vars
};

interface MessagePair {
  user: Message;
  assistant: Message | null;
  userIndex: number;
  assistantIndex: number | null;
}

export const MessagesContainer = ({
  messages,
  loadingStatus,
  onReportStateChange,
  shouldCloseReport = false,
  onReportCloseHandled,
  sx,
  showLanding = false,
  isLogoAnimating = false,
  logoAnimationPhase = 'center',
  onSendMessage,
}: MessagesContainerProps) => {
  const theme = useTheme();
  const { trackEventGA4 } = useContext(AppConfig);
  const { handleSendMessage } = useChatForm(onSendMessage ?? (() => {}));

  const {
    openFeedback,
    thumbsUp,
    currentResponseId,
    feedback,
    createFeedback,
    updateFeedback,
    handleOpenFeedback,
    handleCloseFeedback,
  } = useFeedback(messages);

  const {
    openReport,
    openFilters,
    openTeaser,
    searchParams,
    setSearchParams,
    permalink,
    reportData,
    type,
    bvid,
    handleButtonClick,
    handleCloseReport,
    handleChatTeaserClick,
    handleFiltersClick,
    handleViewAllResultsClick,
    handleOpenFilters,
    handleCloseFilters,
    handleOpenTeaser,
    handleCloseTeaser,
    handleObituaryClick,
    handleRelatedReportClick,
  } = useReportState();

  const sizeColumnOptions = ['500px', '100%'];
  const [sizeIndex, setSizeIndex] = useState(0);
  const [messagesContainerWidth, setMessagesContainerWidth] = useState('100%');
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [lastAutoOpenedMessageContent, setLastAutoOpenedMessageContent] =
    useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { logError } = useContext(AppConfig);
  const isMobileBreakpoint = useMediaQuery('(max-width: 1024px)');
  const isColumnOpen = openReport || openFilters || openTeaser;

  // State for smooth transition
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [landingOpacity, setLandingOpacity] = useState(1);
  const [chatOpacity, setChatOpacity] = useState(0);

  const getReportInfoForMessage = (message: Message) => {
    const potentialOwnerIndex = 0;
    const people = message.reports?.[0]?.reportData?.entities?.people ?? [];
    const mainPerson = people[potentialOwnerIndex];

    const personName = mainPerson?.identity?.names?.[0]?.full ?? 'Unknown';

    return {
      personName,
      reportType: message.reports?.[0]?.type ?? 'unknown',
      potentialOwners: people,
      relatives: mainPerson?.connections?.relatives ?? [],
      addresses: mainPerson?.contact?.addresses ?? [],
      phones: mainPerson?.contact?.phones ?? [],
      emails: mainPerson?.contact?.emails ?? [],
      usernames: mainPerson?.social?.usernames ?? [],
    };
  };

  // Helper function to safely convert data to the expected types
  const getRelatedReportsData = (message: Message) => {
    const reportInfo = getReportInfoForMessage(message);

    // Add missing properties required by RelatedReports class
    const relativesWithBvId = (
      reportInfo.relatives as serializedTypes.Connection[]
    ).map((relative) => ({
      ...relative,
      bvId:
        relative.bvId || relative.identity?.ids?.[0] || `temp-${Math.random()}`, // Add bvId if missing
      name: relative.name || relative.identity?.names?.[0]?.full || 'Unknown', // Add name property
    }));

    const addressesWithSearchable = (
      reportInfo.addresses as commonTypes.Addresses
    ).map((address) => ({
      ...address,
      is_searchable: true, // Mark all addresses as searchable
    }));

    return {
      personName: reportInfo.personName,
      reportType: reportInfo.reportType,
      potentialOwners: reportInfo.potentialOwners as SerializedPerson[],
      relatives: relativesWithBvId,
      addresses: addressesWithSearchable,
      phones: reportInfo.phones as commonTypes.Phones,
      emails: reportInfo.emails as commonTypes.Emails,
      usernames: reportInfo.usernames as commonTypes.Usernames,
    };
  };

  const handleCycleSize = () => {
    setSizeIndex((prev) => (prev + 1) % sizeColumnOptions.length);
  };

  const currentWidth = sizeColumnOptions[sizeIndex];

  // Refs for layout/scroll
  const scrollRootRef = useRef<HTMLDivElement | null>(null);
  const messageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const pairRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [tailGap, setTailGap] = useState(0);
  const resizeObsRef = useRef<ResizeObserver | null>(null);
  const gapRef = useRef<HTMLDivElement | null>(null);
  const typingRef = useRef<HTMLDivElement | null>(null);

  const [stickyIndex, setStickyIndex] = useState<number | null>(null);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);
  const visibleAssistantsRef = useRef<Set<number>>(new Set());

  // state for smooth scroll / anti-loop
  const lastMeasuredScrollHeight = useRef<number>(0); // detects real content growth
  const lastAutoScrollTime = useRef<number>(0);
  const prevPairsCountRef = useRef<number>(0); // detección fiable de "nuevo par"

  // State/refs for smooth scroll
  const smoothScrollRef = useRef<{
    requestAnimationFrameId: number | null;
    target: number | null;
  }>({
    requestAnimationFrameId: null,
    target: null,
  });

  // This is used to scroll to the top of the page when a new pair is added
  const smoothScrollTo = useCallback(
    (
      scrollContainer: HTMLElement,
      targetScrollPosition: number,
      animationDuration = 300
    ) => {
      const scrollAnimationState = smoothScrollRef.current;

      // Cancel any existing animation
      if (scrollAnimationState.requestAnimationFrameId) {
        cancelAnimationFrame(scrollAnimationState.requestAnimationFrameId);
        scrollAnimationState.requestAnimationFrameId = null;
      }

      scrollAnimationState.target = targetScrollPosition;

      const currentScrollPosition = scrollContainer.scrollTop;
      const scrollDistance = targetScrollPosition - currentScrollPosition;
      const animationStartTime = performance.now();

      // Easing function for smooth deceleration
      const easeOutCubic = (progress: number) => 1 - Math.pow(1 - progress, 3);

      const animateScrollStep = (currentTime: number) => {
        // Stop if target has changed
        if (scrollAnimationState.target !== targetScrollPosition) {
          return;
        }

        const animationProgress = Math.min(
          1,
          (currentTime - animationStartTime) / animationDuration
        );
        const newScrollPosition =
          currentScrollPosition +
          scrollDistance * easeOutCubic(animationProgress);

        scrollContainer.scrollTop = newScrollPosition;

        if (animationProgress < 1) {
          // Continue animation
          scrollAnimationState.requestAnimationFrameId =
            requestAnimationFrame(animateScrollStep);
        } else {
          // Animation complete
          scrollAnimationState.requestAnimationFrameId = null;
        }
      };

      // Start the animation
      scrollAnimationState.requestAnimationFrameId =
        requestAnimationFrame(animateScrollStep);
    },
    []
  );

  useEffect(() => {
    if (messages.length > 0 && !isMobileBreakpoint) {
      const lastMessage = messages[messages.length - 1];
      // Check if the last message is from assistant and has a report
      if (
        lastMessage.role === 'assistant' &&
        lastMessage.reports?.[0]?.permalink
      ) {
        // Only auto-open if this is a new report (different from the last one we auto-opened)
        if (lastMessage.content !== lastAutoOpenedMessageContent) {
          // Small delay to ensure the message is fully rendered
          const timer = setTimeout(() => {
            handleButtonClick(lastMessage)(lastMessage.reports?.[0]!);
            setLastAutoOpenedMessageContent(lastMessage.content || null);
          }, 100);

          return () => clearTimeout(timer);
        }
      } else if (lastMessage.role === 'assistant' && lastMessage.params) {
        if (lastMessage.content !== lastAutoOpenedMessageContent) {
          const timer = setTimeout(() => {
            handleViewAllResultsClick(lastMessage)();
            setLastAutoOpenedMessageContent(lastMessage.content || null);
          }, 100);

          return () => clearTimeout(timer);
        }
      }
    }
  }, [
    messages,
    openReport,
    lastAutoOpenedMessageContent,
    handleButtonClick,
    handleViewAllResultsClick,
  ]);

  // Cancel animation if the user interacts
  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root) {
      return;
    }

    const cancelIfAnimating = () => {
      const smoothScrollState = smoothScrollRef.current;
      if (smoothScrollState.requestAnimationFrameId) {
        cancelAnimationFrame(smoothScrollState.requestAnimationFrameId);
        smoothScrollState.requestAnimationFrameId = null;
        smoothScrollState.target = null;
      }
    };

    let scrollTimeout: NodeJS.Timeout;
    const handleUserScroll = () => {
      // Clear previous timeout to debounce scroll events
      clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => {
        const scrollContainer = scrollRootRef.current;
        if (!scrollContainer) return;

        const { scrollTop } = scrollContainer;
        // Use a smaller threshold for better responsiveness
        const shouldShowSmallFont = scrollTop > 30;

        // Only update state if it actually changed to avoid unnecessary re-renders
        setHasUserScrolled((prev) => {
          if (prev !== shouldShowSmallFont) {
            return shouldShowSmallFont;
          }
          return prev;
        });
      }, 10); // Small debounce delay
    };

    root.addEventListener('wheel', cancelIfAnimating, { passive: true });
    root.addEventListener('touchstart', cancelIfAnimating, { passive: true });
    root.addEventListener('scroll', handleUserScroll, { passive: true });

    return () => {
      clearTimeout(scrollTimeout);
      root.removeEventListener('wheel', cancelIfAnimating);
      root.removeEventListener('touchstart', cancelIfAnimating);
      root.removeEventListener('scroll', handleUserScroll);
    };
  }, [stickyIndex]); // Add stickyIndex as dependency

  // Pair User and Assistant Messages
  const pairs: MessagePair[] = [];
  for (let i = 0; i < messages.length; i++) {
    if (messages[i].role === 'user') {
      const userMsg = messages[i];
      const assistantMsg =
        messages[i + 1]?.role === 'assistant' ? messages[i + 1] : null;
      pairs.push({
        user: userMsg,
        assistant: assistantMsg,
        userIndex: i,
        assistantIndex: assistantMsg ? i + 1 : null,
      });
    }
  }

  //-----Use Callbacks-----

  // Calculate scroll position and determine if user is near bottom
  const calculateScrollPosition = useCallback(
    (scrollContainer: HTMLElement) => {
      const maxScrollTop =
        scrollContainer.scrollHeight - scrollContainer.clientHeight;
      const distanceFromBottom = Math.max(
        0,
        maxScrollTop - scrollContainer.scrollTop
      );
      const isNearBottom = Math.ceil(distanceFromBottom) <= 20;

      return { maxScrollTop, distanceFromBottom, isNearBottom };
    },
    []
  );

  // Check if content has grown significantly
  const hasContentGrown = useCallback((scrollContainer: HTMLElement) => {
    const previousHeight = lastMeasuredScrollHeight.current || 0;
    const currentHeight = scrollContainer.scrollHeight;
    const heightGrowth = currentHeight - previousHeight;
    const isSignificantGrowth = heightGrowth > 2;

    lastMeasuredScrollHeight.current = currentHeight;
    return { heightGrowth, isSignificantGrowth };
  }, []);

  // Check if we should ignore auto-scroll (anti-loop protection)
  const shouldIgnoreAutoScroll = useCallback(() => {
    const currentTime = performance.now();
    const timeSinceLastScroll = currentTime - lastAutoScrollTime.current;
    const isTooSoon = timeSinceLastScroll < 48;

    return isTooSoon;
  }, []);

  // Handle auto-scroll based on conditions
  const handleAutoScroll = useCallback(
    (scrollContainer: HTMLElement, hasNewPair: boolean) => {
      const { maxScrollTop, isNearBottom } =
        calculateScrollPosition(scrollContainer);
      const { isSignificantGrowth } = hasContentGrown(scrollContainer);
      const shouldIgnore = shouldIgnoreAutoScroll();

      if (shouldIgnore) {
        return;
      }

      if (hasNewPair) {
        // New pair: smooth animation to bottom
        smoothScrollTo(scrollContainer, maxScrollTop, 300);
        lastAutoScrollTime.current = performance.now();
      } else if (isNearBottom && isSignificantGrowth) {
        // Streaming while near bottom: instant anchor
        scrollContainer.scrollTop = maxScrollTop;
        lastAutoScrollTime.current = performance.now();
      }
    },
    [
      calculateScrollPosition,
      hasContentGrown,
      shouldIgnoreAutoScroll,
      smoothScrollTo,
    ]
  );

  // Calculate the spacer (tailGap) to anchor the last pair to the top
  const adjustScrollGap = useCallback(() => {
    const root = scrollRootRef.current;
    if (!root || pairs.length === 0) {
      return;
    }

    const lastPair = pairs[pairs.length - 1];

    const userElement = messageRefs.current[lastPair.userIndex] || null;
    const assistantElement =
      lastPair.assistantIndex !== null
        ? messageRefs.current[lastPair.assistantIndex!]
        : null;

    const userHeight = userElement?.getBoundingClientRect().height ?? 0;

    let secondHeight = 0;
    if (assistantElement) {
      const assistantHeight = assistantElement.getBoundingClientRect().height;
      const gapHeight = gapRef.current?.getBoundingClientRect().height ?? 0;
      secondHeight = Math.max(0, assistantHeight - gapHeight); // gap está dentro → restar
    } else if (loadingStatus.message !== '') {
      const typingHeight =
        typingRef.current?.getBoundingClientRect().height ?? 0;
      secondHeight = typingHeight;
    }

    const pairHeight = userHeight + secondHeight;
    const gap = Math.max(0, (root?.clientHeight ?? 0) - pairHeight);
    setTailGap(gap);
  }, [pairs.length, loadingStatus.message]);

  //-----Use Handlers-----

  const handleCopyClick = async (content: string, messageIndex: number) => {
    try {
      const success = await copyProcessedText(content);
      if (success) {
        trackEventGA4({
          event_name: CHATBV_GA4_EVENTS.name,
          type: CHATBV_GA4_EVENTS.message.type,
          subtype: CHATBV_GA4_EVENTS.message.subTypes.copy_response,
        });
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        setCopiedIndex(messageIndex);
        timeoutRef.current = setTimeout(() => setCopiedIndex(null), 1000);
      }
    } catch (error) {
      logError('Error copying text', error as Error);
    }
  };

  //-----Use Effects-----

  // Handle smooth transition between landing and chat
  useEffect(() => {
    if (showLanding) {
      // When showing landing, fade in landing and fade out chat
      setLandingOpacity(1);
      setChatOpacity(0);
      setIsTransitioning(false);
    } else {
      // When hiding landing, show chat immediately
      setLandingOpacity(0);
      setChatOpacity(1);
      setIsTransitioning(false);
    }
  }, [showLanding]);

  // Clear timeout on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  // Handle Messages Container Width
  useEffect(() => {
    if ((openReport || openFilters || openTeaser) && currentWidth === '100%') {
      setMessagesContainerWidth('19%');
    } else if (
      (openReport || openFilters || openTeaser) &&
      currentWidth === '500px'
    ) {
      setMessagesContainerWidth('60%');
    } else {
      setMessagesContainerWidth('100%');
    }
  }, [openReport, openFilters, openTeaser, currentWidth]);

  // Handle Report State Change
  useEffect(
    () => onReportStateChange?.(openReport),
    [openReport, onReportStateChange]
  );

  // Handle external report close request
  useEffect(() => {
    if (shouldCloseReport && openReport) {
      handleCloseReport();
      onReportCloseHandled?.();
    }
  }, [shouldCloseReport, openReport, handleCloseReport, onReportCloseHandled]);

  // ResizeObserver to keep layout consistent
  // This is used to keep the layout consistent when the window is resized
  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root || pairs.length === 0) {
      return;
    }

    // Detect new pair by counting
    const gotNewPair = pairs.length > prevPairsCountRef.current;
    prevPairsCountRef.current = pairs.length;

    // Last pair elements already mounted
    const lastPair = pairs[pairs.length - 1];
    const userElement = messageRefs.current[lastPair.userIndex] || null;
    const assistantElement =
      lastPair.assistantIndex !== null
        ? messageRefs.current[lastPair.assistantIndex!]
        : null;
    const typingElement =
      lastPair.assistantIndex === null ? typingRef.current : null;

    if (resizeObsRef.current) {
      resizeObsRef.current.disconnect();
    }
    const resizeObserver = new ResizeObserver(() => {
      // Let browser apply layout changes first
      requestAnimationFrame(() => {
        adjustScrollGap();

        // Now handle scroll after layout is stable
        requestAnimationFrame(() => {
          handleAutoScroll(root, gotNewPair);
        });
      });
    });

    resizeObserver.observe(root);
    if (userElement) {
      resizeObserver.observe(userElement);
    }
    if (assistantElement) {
      resizeObserver.observe(assistantElement);
    }
    if (!assistantElement && typingElement) {
      resizeObserver.observe(typingElement);
    }

    resizeObsRef.current = resizeObserver;

    // Initial calculation and anchor
    requestAnimationFrame(() => {
      adjustScrollGap();
      requestAnimationFrame(() => {
        lastMeasuredScrollHeight.current = root.scrollHeight;
        handleAutoScroll(root, gotNewPair);
      });
    });

    return () => resizeObserver.disconnect();
  }, [
    pairs.length,
    adjustScrollGap,
    openReport,
    messagesContainerWidth,
    smoothScrollTo,
  ]);

  // Sticky header
  useEffect(() => {
    // Clear the previous set of visible assistants
    visibleAssistantsRef.current.clear();

    // Create intersection observer to detect visible messages
    const intersectionObserver = new IntersectionObserver(
      (intersectionEntries) => {
        intersectionEntries.forEach((entry) => {
          const currentAssistantIndex = Number(
            entry.target.getAttribute('data-index')
          );
          if (Number.isFinite(currentAssistantIndex)) {
            if (entry.isIntersecting) {
              visibleAssistantsRef.current.add(currentAssistantIndex);
            } else {
              visibleAssistantsRef.current.delete(currentAssistantIndex);
            }
          }
        });

        // Calculate the sticky index based on the visible assistants
        if (visibleAssistantsRef.current.size > 0) {
          const visibleUserMessageIndexes = Array.from(
            visibleAssistantsRef.current
          )
            .map((visibleAssistantIndex) => {
              const messagePair = pairs.find(
                (pair) => pair.assistantIndex === visibleAssistantIndex
              );
              return messagePair ? messagePair.userIndex : null;
            })
            .filter((userIndex): userIndex is number => userIndex !== null);

          if (visibleUserMessageIndexes.length > 0) {
            const minimumUserIndex = Math.min(...visibleUserMessageIndexes);
            setStickyIndex(minimumUserIndex);
          } else {
            setStickyIndex(null);
          }
        } else {
          setStickyIndex(null);
        }
      },
      { root: scrollRootRef.current || null, threshold: 0.1 }
    );

    // Observe each assistant message to detect visibility
    pairs.forEach((messagePair) => {
      if (messagePair.assistantIndex !== null) {
        const assistantMessageElement =
          messageRefs.current[messagePair.assistantIndex];
        if (assistantMessageElement) {
          assistantMessageElement.setAttribute(
            'data-index',
            messagePair.assistantIndex.toString()
          );
          intersectionObserver.observe(assistantMessageElement);
        }
      }
    });

    // Clean up the observer when unmounting
    return () => intersectionObserver.disconnect();
  }, [messages]);

  const baseMessageBoxContainerStyles = {
    display: 'block',
    width: '100%',
    maxWidth: '740px',
    backgroundColor: theme.palette.background.paper,
    margin: 0,
    padding: theme.spacing(1, 0),
    overflowWrap: 'break-word',
    boxSizing: 'border-box',
  };

  const stickyMessageBoxContainerStyles = {
    ...baseMessageBoxContainerStyles,
    position: 'sticky',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    padding: theme.spacing(0.75, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    overflowWrap: 'break-word',
    transition: 'all 0.2s ease-in-out',
  };

  const messageHasFeedbackRecord =
    typeof feedback[currentResponseId]?.thumbs_up === 'boolean';

  return (
    <Stack
      direction={isColumnOpen ? 'row' : 'column'}
      width="100%"
      height="100%"
    >
      {!isMobileBreakpoint || !isColumnOpen ? (
        <ChatPageContainerStack
          spacing={2}
          sx={{
            width: messagesContainerWidth,
            height: isColumnOpen ? '98%' : '100%',
            position: 'relative',
            ...sx,
          }}
        >
          <LandingSection
            showLanding={showLanding}
            landingOpacity={landingOpacity}
            isTransitioning={isTransitioning}
            isLogoAnimating={isLogoAnimating}
            logoAnimationPhase={logoAnimationPhase}
            openReport={openReport}
            onSendMessage={handleSendMessage}
          />

          {!showLanding && (
            <MessagesContentStack
              ref={scrollRootRef}
              sx={{
                opacity: chatOpacity,
                transition: 'opacity 0.5s cubic-bezier(0.4, 0.0, 0.2, 1)',
                position: 'relative',
                zIndex: 1,
                pointerEvents: chatOpacity === 0 ? 'none' : 'auto',
              }}
            >
              {pairs.map((pair, index) => (
                <Stack
                  key={index}
                  ref={(el) => {
                    pairRefs.current[index] = el;
                  }}
                  width="100%"
                  justifyContent="center"
                  alignItems="center"
                >
                  <MessageBoxContainer
                    ref={(el: HTMLDivElement | null) => {
                      messageRefs.current[pair.userIndex] = el;
                    }}
                    sx={
                      stickyIndex === pair.userIndex
                        ? stickyMessageBoxContainerStyles
                        : baseMessageBoxContainerStyles
                    }
                  >
                    <MessageContent
                      content={pair.user.content}
                      isHeader={true}
                      isSticky={stickyIndex === pair.userIndex}
                      hasUserScrolled={hasUserScrolled}
                      handleButtonClick={handleButtonClick(pair.user)}
                      handleChatTeaserClick={handleChatTeaserClick(pair.user)}
                      handleFiltersClick={handleFiltersClick(pair.user)}
                      handleViewAllResultsClick={handleViewAllResultsClick(
                        pair.user
                      )}
                      handleFollowUpButtonClick={() => {}}
                      reports={pair.user.reports}
                      ContainerStyles={
                        stickyIndex === pair.userIndex
                          ? {
                              fontSize: '0.95rem',
                              display: 'block',
                              width: '100%',
                              maxWidth: '100%',
                            }
                          : {
                              display: 'block',
                              width: '100%',
                              maxWidth: '100%',
                            }
                      }
                      teaser={pair.user.teaser}
                      params={pair.user.params}
                      messagePermalink={pair.user.permalink}
                      currentBvid={bvid}
                      currentPermalink={permalink}
                      reportOpen={openReport}
                    />
                  </MessageBoxContainer>

                  {pair.assistant ? (
                    <MessageBoxContainer
                      ref={(el: HTMLDivElement | null) => {
                        messageRefs.current[pair.assistantIndex!] = el;
                      }}
                      sx={{
                        width: '100%',
                        maxWidth: '740px',
                      }}
                    >
                      <MessageContent
                        content={pair.assistant.content}
                        isHeader={false}
                        isSticky={false}
                        hasUserScrolled={hasUserScrolled}
                        handleButtonClick={handleButtonClick(pair.assistant!)}
                        handleChatTeaserClick={handleChatTeaserClick(
                          pair.assistant!
                        )}
                        handleFiltersClick={handleFiltersClick(pair.assistant!)}
                        handleViewAllResultsClick={handleViewAllResultsClick(
                          pair.assistant!
                        )}
                        handleFollowUpButtonClick={() => {}}
                        teaser={pair.assistant.teaser}
                        currentBvid={bvid}
                        reports={pair.assistant.reports}
                        params={pair.assistant.params}
                        messagePermalink={pair.assistant.permalink}
                        currentPermalink={permalink}
                        reportOpen={openReport}
                      />
                      <Box>
                        <Stack
                          direction={'row'}
                          spacing={2}
                          justifyContent={'end'}
                        >
                          {feedback[pair.assistant.response_id || '']
                            ?.thumbs_up ? (
                            <ThumbUp
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '16px',
                                cursor: 'pointer',
                              }}
                              onClick={handleOpenFeedback(
                                true,
                                pair.assistant.response_id || ''
                              )}
                            />
                          ) : (
                            <ThumbUpOffAlt
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '16px',
                                cursor: 'pointer',
                              }}
                              onClick={handleOpenFeedback(
                                true,
                                pair.assistant.response_id || ''
                              )}
                            />
                          )}
                          {feedback[pair.assistant.response_id || '']
                            ?.thumbs_up === false ? (
                            <ThumbDown
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '16px',
                                cursor: 'pointer',
                              }}
                              onClick={handleOpenFeedback(
                                false,
                                pair.assistant.response_id || ''
                              )}
                            />
                          ) : (
                            <ThumbDownOffAlt
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '16px',
                                cursor: 'pointer',
                              }}
                              onClick={handleOpenFeedback(
                                false,
                                pair.assistant.response_id || ''
                              )}
                            />
                          )}
                          <DarkTooltip
                            title="Copied!"
                            disableHoverListener
                            disableFocusListener
                            open={copiedIndex === pair.assistantIndex}
                          >
                            {copiedIndex === pair.assistantIndex ? (
                              <FileCopy
                                sx={{
                                  color: theme.palette.text.secondary,
                                  fontSize: '16px',
                                }}
                              />
                            ) : (
                              <FileCopyOutlined
                                onClick={() =>
                                  handleCopyClick(
                                    pair.assistant!.content,
                                    pair.assistantIndex!
                                  )
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleCopyClick(
                                      pair.assistant!.content,
                                      pair.assistantIndex!
                                    );
                                  }
                                }}
                                aria-label="Copy message"
                                tabIndex={3}
                                sx={{
                                  color: theme.palette.text.secondary,
                                  fontSize: '16px',
                                  cursor: 'pointer',
                                }}
                              />
                            )}
                          </DarkTooltip>
                        </Stack>
                        {pair.assistant?.reports?.[0]?.permalink &&
                        pair.assistant?.reports?.[0]?.reportData &&
                        pair.assistant?.reports?.[0]?.type ? (
                          <RelatedReportsChat
                            {...getRelatedReportsData(pair.assistant)}
                            handleRelatedReportClick={handleRelatedReportClick}
                          />
                        ) : (
                          <MessageContainerDivider />
                        )}
                      </Box>

                      {/* Tail Gap inside the last block */}
                      {index === pairs.length - 1 && (
                        <Box
                          ref={(el: HTMLDivElement | null) => {
                            gapRef.current = el;
                          }}
                          sx={{ height: `${tailGap}px`, width: '100%' }}
                        />
                      )}
                    </MessageBoxContainer>
                  ) : (
                    <>
                      <Box
                        ref={typingRef}
                        sx={{ width: '100%', maxWidth: '740px' }}
                      >
                        <TypingIndicator loadingStatus={loadingStatus} />
                      </Box>
                      {index === pairs.length - 1 && (
                        <Box
                          ref={(el: HTMLDivElement | null) => {
                            gapRef.current = el;
                          }}
                          sx={{ height: `${tailGap}px`, width: '100%' }}
                        />
                      )}
                    </>
                  )}
                </Stack>
              ))}
            </MessagesContentStack>
          )}
          {!isMobileBreakpoint && !showLanding && (
            <ChatInput
              isReportOpen={openReport}
              isLoading={loadingStatus !== LOADING_STATUS.IDLE}
            />
          )}
        </ChatPageContainerStack>
      ) : null}

      {openReport && (
        <ReportColumn
          reportData={reportData}
          onClose={handleCloseReport}
          permalink={permalink}
          currentWidth={isMobileBreakpoint ? '100%' : currentWidth}
          handleCycleSize={handleCycleSize}
          type={type}
        />
      )}
      {openFilters && (
        <FiltersColumn
          currentWidth={isMobileBreakpoint ? '100%' : currentWidth}
          isMobileBreakpoint={isMobileBreakpoint}
          onClose={handleCloseFilters}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          handleOpenTeaser={handleOpenTeaser}
        />
      )}
      {openTeaser && searchParams.lName && (
        <TeaserColumn
          currentWidth={isMobileBreakpoint ? '100%' : currentWidth}
          isMobileBreakpoint={isMobileBreakpoint}
          onClose={handleCloseTeaser}
          searchData={searchParams}
          handleOpenFilters={handleOpenFilters}
          handlePersonReport={handleChatTeaserClick(null, 'person')}
          handleObituaryReport={handleObituaryClick}
        />
      )}
      <ChatFeedbackModal
        open={openFeedback}
        onClose={handleCloseFeedback}
        thumbsUp={thumbsUp}
        response_item_id={currentResponseId}
        createFeedback={createFeedback}
        updateFeedback={updateFeedback}
        hasRecord={messageHasFeedbackRecord}
        feedbackText={feedback[currentResponseId]?.feedback_text || ''}
      />
    </Stack>
  );
};
